import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './SearchOrdersPage.css';
import { FilePreview } from '../components/FileViewer';
import PrintButton from '../components/PrintButton';
import { API_URL } from '../config';

const API_BASE_URL = `${API_URL}/api`;

const SearchOrdersPageB24 = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [debugMode, setDebugMode] = useState(false);
  const [fileErrors, setFileErrors] = useState({});
  const [refreshingFile, setRefreshingFile] = useState(null);
  const [userBranch, setUserBranch] = useState(null);
  const [hasFullAccess, setHasFullAccess] = useState(false);

  const [userRole, setUserRole] = useState(null);
  const [orderTimeline, setOrderTimeline] = useState({}); // Храним timeline для заказов
  const [orderTrips, setOrderTrips] = useState({});       // Рейсы закалки для заказов

  // Состояния для изменения стадии (только для admin)
  const [updatingStage, setUpdatingStage] = useState(null); // ID заказа, для которого меняем стадию
  const [stageUpdateError, setStageUpdateError] = useState(null);

  // Новые состояния для поиска по деталям
  const [searchMode, setSearchMode] = useState('order'); // 'order' или 'details'
  const [detailWidth, setDetailWidth] = useState('');
  const [detailHeight, setDetailHeight] = useState('');
  const [tolerance, setTolerance] = useState(50); // Допуск в мм
  const [detailSearchResults, setDetailSearchResults] = useState([]);

  // Дополнительные фильтры для поиска по деталям
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [glassType, setGlassType] = useState('');
  const [glassThickness, setGlassThickness] = useState('');

  // Получение информации о пользователе при загрузке компонента
  useEffect(() => {
    const getUserInfo = async () => {
      try {
        const token = localStorage.getItem('token');
        const userStr = localStorage.getItem('user');
        const user = userStr ? JSON.parse(userStr) : {};
        const userName = user.username;
        const userEmail = user.email;
        const userRole = user.role;
        setUserRole(userRole);
        
        // Проверяем, имеет ли пользователь полный доступ (все роли кроме franchise)
        const fullAccess = userRole !== 'franchise';
        setHasFullAccess(fullAccess);
        
        console.log('Отладка определения филиала:');
        console.log('- userName:', userName);
        console.log('- userEmail:', userEmail);
        console.log('- userRole:', userRole);
        console.log('- fullAccess:', fullAccess);
        
        // Для роли franchise используем имя пользователя как префикс филиала
        if (userRole === 'franchise' && userName) {
          const branch = userName.toUpperCase();
          console.log(`✅ Роль franchise: используем имя пользователя "${userName}" как префикс филиала: "${branch}"`);
          setUserBranch(branch);
          return;
        }
        
        // Для остальных ролей пытаемся автоматически определить префикс
        if (!fullAccess) {
          const sources = [userName, userEmail].filter(Boolean);
          
          for (const source of sources) {
            console.log(`Проверяем источник "${source}"`);
            
            // Расширенные паттерны для поиска префикса филиала
            const patterns = [
              /([A-Z]{2,4}\d*)/,           // AKB1, MOS2, SPB3
              /([A-Z]{3,4})/,              // AKB, MOS, SPB
              /([A-Z]{2,4}\d*)-/,          // AKB1-, MOS2-
              /^([A-Z]{2,4}\d*)/,          // Начинается с AKB1, MOS2
              /([A-Z]{2,4}\d*)@/,          // AKB1@, MOS2@ (для email)
              /^([a-z]{2,4}\d*)$/,         // alm3, akb1, mos2 (в нижнем регистре)
            ];
            
            for (const pattern of patterns) {
              const match = source.match(pattern);
              if (match) {
                const branch = match[1].toUpperCase();
                console.log(`✅ Найден префикс филиала: "${branch}" из "${source}" паттерном ${pattern}`);
                setUserBranch(branch);
                return;
              }
            }
          }
          
          console.log('❌ Не удалось определить префикс филиала для ограниченной роли');
        } else {
          console.log('✅ Пользователь имеет полный доступ к поиску (роль не franchise)');
        }
        
        setUserBranch(null);
        
      } catch (error) {
        console.error('Ошибка получения информации о пользователе:', error);
      }
    };

    getUserInfo();
  }, []);


  // Функция для проверки валидности файла
  const isValidFile = (file) => {
    return file && typeof file === 'object' && file.id;
  };

  // Улучшенная функция форматирования файлов
  const formatFiles = (filesArray) => {
    if (!filesArray || !Array.isArray(filesArray)) return [];
    
    return filesArray
      .filter(file => isValidFile(file))
      .map(file => ({
        id: String(file.id),
        name: file.name || `Чертеж ${file.id}`,
        url: file.urlMachine || file.url || null
      }));
  };

  // Функция для умного поиска с учетом филиала
  const formatSearchQuery = (query) => {
    const trimmedQuery = query.trim();
    
    // Если пользователь имеет полный доступ, возвращаем запрос как есть
    if (hasFullAccess) {
      return trimmedQuery;
    }
    
    // Если у нас нет префикса филиала, возвращаем как есть
    if (!userBranch) {
      return trimmedQuery;
    }
    
    // Если запрос только цифры - добавляем префикс пользователя
    if (/^\d+$/.test(trimmedQuery)) {
      const formattedQuery = `${userBranch}-${trimmedQuery}`;
      console.log(`Умный поиск: "${trimmedQuery}" -> "${formattedQuery}"`);
      return formattedQuery;
    }
    
    // Если запрос уже содержит тире (полный формат), возвращаем как есть
    if (trimmedQuery.includes('-')) {
      return trimmedQuery;
    }
    
    // Если запрос содержит буквы и цифры, но без тире
    if (/^[A-Z]{2,4}\d+$/.test(trimmedQuery)) {
      return `${trimmedQuery}-`;
    }
    
    return trimmedQuery;
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    // Если нет префикса филиала и нет полного доступа, предупреждаем пользователя
    if (!userBranch && !hasFullAccess) {
      setError('Не удалось определить префикс филиала из имени пользователя. Обратитесь к администратору.');
      return;
    }
    
    setLoading(true);
    setError(null);
    setSearchResults([]);
    setExpandedOrder(null);
    setFileErrors({});
    
    const token = localStorage.getItem('token');
    const formattedQuery = formatSearchQuery(searchQuery);
    
    try {
      console.log(`Поиск заказов B24 по запросу: "${formattedQuery}" ${hasFullAccess ? '(полный доступ)' : `для филиала: ${userBranch}`}`);
      
      // Используем специальный endpoint для B24 поиска
      const response = await axios.get(`${API_BASE_URL}/search-orders-b24`, {
        params: {
          query: formattedQuery,
          workplace: 1036,  // Цифровое рабочее место
          funnels: [6, 7],  // Воронки 6 и 7
          userBranch: hasFullAccess ? null : userBranch, // Передаем филиал только для ограниченного доступа
          filterByUser: !hasFullAccess  // Фильтрация только для пользователей без полного доступа
        },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success && response.data.results.length > 0) {
        // ОТЛАДКА: Выводим полную структуру первого заказа
        console.log('ПОЛНАЯ СТРУКТУРА ЗАКАЗА:', response.data.results[0]);
        console.log('ВСЕ ПОЛЯ ЗАКАЗА:', Object.keys(response.data.results[0]));
        
        // Дополнительная фильтрация на клиенте для безопасности (только для ограниченного доступа)
        const userOrders = hasFullAccess ? response.data.results : response.data.results.filter(order => {
          const orderNumber = order.orderNumber || '';
          return orderNumber.startsWith(userBranch);
        });
        
        // Форматируем файлы перед установкой результатов
        const formattedResults = userOrders.map(order => {
          const formattedMainFiles = formatFiles(order.files || []);
          const formattedProdFiles = formatFiles(order.productionFiles || []);
          
          if (debugMode) {
            console.log(`Заказ ${order.id} (B24):`, {
              исходныеДизайнФайлы: order.files,
              исходныеДеталировкаФайлы: order.productionFiles,
              отформатированныеДизайнФайлы: formattedMainFiles,
              отформатированныеДеталировкаФайлы: formattedProdFiles
            });
          }
          
          // Всегда выводим отладочную информацию о файлах
          console.log(`ОТЛАДКА ФАЙЛОВ для заказа ${order.id}:`);
          console.log('Исходные files (Дизайн):', order.files);
          console.log('Исходные productionFiles (Деталировка):', order.productionFiles);
          console.log('Отформатированные дизайн файлы:', formattedMainFiles);
          console.log('Отформатированные деталировка файлы:', formattedProdFiles);
          
          return {
            ...order,
            files: formattedMainFiles,
            productionFiles: formattedProdFiles
          };
        });
        
        console.log(`Результаты поиска B24 ${hasFullAccess ? '(полный доступ)' : `для филиала ${userBranch}`}:`, formattedResults);
        setSearchResults(formattedResults);
        
        if (formattedResults.length === 0) {
          setError(hasFullAccess ? 
            'Заказы не найдены. Проверьте номер заказа.' : 
            `Заказы с префиксом "${userBranch}" не найдены. Проверьте номер заказа.`);
        }
      } else {
        setError(hasFullAccess ? 
          'Заказы не найдены в цифровом рабочем месте 1036' : 
          `Заказы с префиксом "${userBranch}" не найдены в цифровом рабочем месте 1036`);
      }
    } catch (error) {
      console.error('Ошибка при поиске заказов B24:', error);
      setError(`Ошибка при поиске: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Функция для поиска по деталям (fuzzy search по размерам)
  const handleDetailSearch = async () => {
    if (!detailWidth || !detailHeight) {
      setError('Введите ширину и высоту детали для поиска');
      return;
    }

    setLoading(true);
    setError(null);
    setDetailSearchResults([]);
    setExpandedOrder(null);

    const token = localStorage.getItem('token');

    try {
      console.log(`Поиск по деталям: ${detailWidth}x${detailHeight}, допуск: ${tolerance}мм`, {
        dateFrom, dateTo, glassType, glassThickness
      });

      const response = await axios.get(`${API_BASE_URL}/search-by-details`, {
        params: {
          width: detailWidth,
          height: detailHeight,
          tolerance: tolerance,
          limit: 50,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          glassType: glassType || undefined,
          glassThickness: glassThickness || undefined
        },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success && response.data.results.length > 0) {
        console.log(`Найдено ${response.data.results.length} заказов с похожими деталями`);
        setDetailSearchResults(response.data.results);
      } else {
        let errorMsg = `Заказы с деталями похожими на ${detailWidth}x${detailHeight}мм не найдены`;
        if (glassType || glassThickness || dateFrom || dateTo) {
          errorMsg += '. Попробуйте изменить фильтры.';
        }
        setError(errorMsg);
      }
    } catch (error) {
      console.error('Ошибка при поиске по деталям:', error);
      setError(`Ошибка при поиске: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Функция получения цвета для процента схожести
  const getSimilarityColor = (similarity) => {
    if (similarity >= 90) return 'bg-green-500 text-white';
    if (similarity >= 70) return 'bg-green-400 text-white';
    if (similarity >= 50) return 'bg-yellow-400 text-black';
    if (similarity >= 30) return 'bg-orange-400 text-white';
    return 'bg-red-400 text-white';
  };

  // Функция для обновления файла в кеше сервера
  const handleRefreshFile = async (fileId) => {
    setRefreshingFile(fileId);
    
    try {
      const token = localStorage.getItem('token');
      
      await axios.delete(`${API_BASE_URL}/cache/${fileId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setFileErrors(prev => ({
        ...prev,
        [fileId]: null
      }));
      
      alert(`Кеш для файла ${fileId} успешно обновлен. Пожалуйста, повторите попытку просмотра.`);
    } catch (error) {
      console.error(`Ошибка при обновлении файла ${fileId}:`, error);
      alert(`Ошибка при обновлении файла: ${error.message}`);
    } finally {
      setRefreshingFile(null);
    }
  };
  
  // Функция для отображения ошибки файла и кнопки обновления
  const handleFileError = (fileId, errorMessage) => {
    setFileErrors(prev => ({
      ...prev,
      [fileId]: errorMessage
    }));
  };
  
  // Загрузка рейсов закалки для заказа
  const fetchOrderTrips = async (orderId, orderNumber) => {
    if (orderTrips[orderId] !== undefined) return;
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API_BASE_URL}/zakalka-trips/by-order/${encodeURIComponent(orderNumber)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        setOrderTrips(prev => ({ ...prev, [orderId]: response.data.trips }));
      }
    } catch (error) {
      console.error('Ошибка получения рейсов для заказа:', error);
      setOrderTrips(prev => ({ ...prev, [orderId]: [] }));
    }
  };

  // Функция для расширения/скрытия файлов заказа
  const toggleOrderExpand = (orderId, orderNumber) => {
    if (expandedOrder === orderId) {
      setExpandedOrder(null);
    } else {
      setExpandedOrder(orderId);
      // Загружаем timeline и рейсы при раскрытии заказа
      fetchOrderTimeline(orderId);
      if (orderNumber) fetchOrderTrips(orderId, orderNumber);
    }
  };

  // Словарь стадий для фильтров и отображения
  const stageMap = {
    // Воронка 6 (цифровое рабочее место 1036) - Основные стадии
    'DT1036_6:NEW': 'Новая заявка',
    'DT1036_6:UC_58B1IG': 'Резка стекла',
    'DT1036_6:UC_HD3BPN': 'Обработка стекла', 
    'DT1036_6:UC_0JY06B': 'Арм 1',
    'DT1036_6:UC_TACW0R': 'Готово К закалке',
    'DT1036_6:UC_PBEINX': 'На закалке',
    'DT1036_6:UC_RN2FGC': 'Арм 2',
    'DT1036_6:UC_4FXLEO': 'Матировка',
    'DT1036_6:UC_5GKJLH': 'Арм 3',
    'DT1036_6:UC_WE1KJE': 'Сборка лофта / Зеркала',
    'DT1036_6:UC_EVJQPD': 'Арм 4',
    'DT1036_6:UC_SNLWF9': 'Упаковка',
    'DT1036_6:UC_NNZ8TM': 'Арм 5',
    'DT1036_6:UC_ZZP9IY': 'Комплектация',
    'DT1036_6:SUCCESS': 'Выполнен',
    'DT1036_6:FAIL': 'Провал',
    'DT1036_6:UC_D7FPJS': 'Переделка',
    
    // Воронка 7 (цифровое рабочее место 1036)
    'DT1036_7:NEW': 'АРМ 1 ВОРОНКА 2',
    'DT1036_7:UC_PROCESSING': 'Готово нет оплаты',
    'DT1036_7:UC_ASSEMBLY': 'Готово к выдаче',
    'DT1036_7:UC_TESTING': 'Готово к доставке',
    'DT1036_7:UC_PACKAGING': 'Передан в службу доставки',
    'DT1036_7:UC_DELIVERY': 'Готово к установке',
    'DT1036_7:SUCCESS': 'Завершен'
  };

  // Функция для получения названия стадии по ID для воронок 6 и 7
  const getStageName = (stageId) => {
    return stageMap[stageId] || stageId;
  };

  // Функция для получения истории timeline заказа
  const fetchOrderTimeline = async (orderId) => {
    if (orderTimeline[orderId]) {
      return; // Уже загружена
    }

    const token = localStorage.getItem('token');
    
    try {
      console.log(`Загружаем timeline для заказа ${orderId}`);
      
      const response = await axios.get(`${API_BASE_URL}/order-timeline/${orderId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setOrderTimeline(prev => ({
          ...prev,
          [orderId]: response.data.timeline || []
        }));
      }
    } catch (error) {
      console.error(`Ошибка получения timeline для заказа ${orderId}:`, error);
      setOrderTimeline(prev => ({
        ...prev,
        [orderId]: []
      }));
    }
  };

  // Функция для изменения стадии заказа (только для admin)
  const handleStageUpdate = async (orderId, newStageId) => {
    if (userRole !== 'admin') {
      setStageUpdateError('Только администратор может изменять стадию заказа');
      return;
    }

    setUpdatingStage(orderId);
    setStageUpdateError(null);

    const token = localStorage.getItem('token');

    try {
      console.log(`Администратор изменяет стадию заказа ${orderId} на ${newStageId}`);

      const response = await axios.post(`${API_BASE_URL}/admin/update-stage`, {
        itemId: orderId,
        targetStageId: newStageId
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        // Обновляем стадию в локальных результатах поиска
        setSearchResults(prev => prev.map(order => {
          if (order.id === orderId) {
            return { ...order, stageId: newStageId };
          }
          return order;
        }));

        // Очищаем timeline чтобы при раскрытии загрузился заново
        setOrderTimeline(prev => {
          const newTimeline = { ...prev };
          delete newTimeline[orderId];
          return newTimeline;
        });

        console.log(`Стадия заказа ${orderId} успешно изменена на ${newStageId}`);
      } else {
        setStageUpdateError(response.data.message || 'Не удалось изменить стадию');
      }
    } catch (error) {
      console.error('Ошибка при изменении стадии:', error);
      setStageUpdateError(error.response?.data?.message || error.message);
    } finally {
      setUpdatingStage(null);
    }
  };

  // Список доступных стадий для админа
  const adminStageOptions = [
    { id: 'DT1036_6:NEW', name: 'Новая заявка', category: 'Производство' },
    { id: 'DT1036_6:UC_58B1IG', name: 'Резка стекла', category: 'Производство' },
    { id: 'DT1036_6:UC_HD3BPN', name: 'Обработка стекла', category: 'Производство' },
    { id: 'DT1036_6:UC_0JY06B', name: 'Арм 1', category: 'Производство' },
    { id: 'DT1036_6:UC_TACW0R', name: 'Готово к закалке', category: 'Производство' },
    { id: 'DT1036_6:UC_PBEINX', name: 'На закалке', category: 'Производство' },
    { id: 'DT1036_6:UC_RN2FGC', name: 'Арм 2', category: 'Производство' },
    { id: 'DT1036_6:UC_4FXLEO', name: 'Матировка', category: 'Производство' },
    { id: 'DT1036_6:UC_5GKJLH', name: 'Арм 3', category: 'Производство' },
    { id: 'DT1036_6:UC_WE1KJE', name: 'Сборка лофта / Зеркала', category: 'Производство' },
    { id: 'DT1036_6:UC_EVJQPD', name: 'Арм 4', category: 'Производство' },
    { id: 'DT1036_6:UC_SNLWF9', name: 'Упаковка', category: 'Производство' },
    { id: 'DT1036_6:UC_NNZ8TM', name: 'Арм 5', category: 'Производство' },
    { id: 'DT1036_6:UC_ZZP9IY', name: 'Комплектация', category: 'Производство' },
    { id: 'DT1036_6:SUCCESS', name: 'Выполнен', category: 'Производство' },
    { id: 'DT1036_6:FAIL', name: 'Провал', category: 'Производство' },
    { id: 'DT1036_6:UC_D7FPJS', name: 'Переделка', category: 'Производство' },
    { id: 'DT1036_7:NEW', name: 'АРМ 1 ВОРОНКА 2', category: 'Доставка' },
    { id: 'DT1036_7:UC_PROCESSING', name: 'Готово нет оплаты', category: 'Доставка' },
    { id: 'DT1036_7:UC_ASSEMBLY', name: 'Готово к выдаче', category: 'Доставка' },
    { id: 'DT1036_7:UC_TESTING', name: 'Готово к доставке', category: 'Доставка' },
    { id: 'DT1036_7:UC_PACKAGING', name: 'Передан в службу доставки', category: 'Доставка' },
    { id: 'DT1036_7:UC_DELIVERY', name: 'Готово к установке', category: 'Доставка' },
    { id: 'DT1036_7:SUCCESS', name: 'Завершен', category: 'Доставка' },
  ];

  return (
    <div className="search-page-container">
     
      
      
      
      {/* Информация о пользователе для ограниченного доступа */}
      {!hasFullAccess && userBranch && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Ваш филиал:</strong> {userBranch}
          </p>
          <p className="text-xs text-blue-600 mt-1">
            При вводе только номера заказа (например, "24") автоматически добавится код филиала: "{userBranch}-24"
          </p>
          <p className="text-xs text-blue-600 mt-1">
            <strong>Показываются только ваши заказы</strong> с префиксом "{userBranch}"
          </p>
        </div>
      )}
      
      {/* Сообщение если не удалось определить филиал для пользователей без полного доступа */}
      {!userBranch && !hasFullAccess && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800 mb-2">
            <strong>Не удалось определить ваш филиал автоматически</strong>
          </p>
          <p className="text-xs text-red-600">
            Обратитесь к администратору для настройки имени пользователя с префиксом филиала (например: alm3, akb1, mos2)
          </p>
        </div>
      )}

      {/* Переключатель режимов поиска */}
      <div className="mb-4 flex space-x-2">
        <button
          type="button"
          onClick={() => {
            setSearchMode('order');
            setDetailSearchResults([]);
            setError(null);
          }}
          className={`px-2 py-1 rounded-lg font-medium transition-colors ${
            searchMode === 'order'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Поиск по номеру заказа
        </button>
        <button
          type="button"
          onClick={() => {
            setSearchMode('details');
            setSearchResults([]);
            setError(null);
          }}
          className={`px-2 py-1 rounded-lg font-medium transition-colors ${
            searchMode === 'details'
              ? 'bg-purple-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Поиск по деталям
        </button>
      </div>

      {/* Форма поиска по деталям */}
      {searchMode === 'details' && (
        <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
          <h3 className="text-lg font-medium text-purple-800 mb-3">Поиск по размерам детали</h3>

          {/* Основные параметры размеров */}
          <div className="flex flex-wrap gap-4 items-end mb-4">
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Ширина (мм)</label>
              <input
                type="number"
                value={detailWidth}
                onChange={(e) => setDetailWidth(e.target.value)}
                placeholder="например: 880"
                className="w-32 px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
            <div className="text-2xl text-purple-400 pb-2">×</div>
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Высота (мм)</label>
              <input
                type="number"
                value={detailHeight}
                onChange={(e) => setDetailHeight(e.target.value)}
                placeholder="например: 1990"
                className="w-28 px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Допуск (±мм)</label>
              <select
                value={tolerance}
                onChange={(e) => setTolerance(parseInt(e.target.value))}
                className="w-24 px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="10">±10</option>
                <option value="20">±20</option>
                <option value="30">±30</option>
                <option value="50">±50</option>
                <option value="100">±100</option>
              </select>
            </div>
          </div>

          {/* Дополнительные фильтры */}
          <div className="flex flex-wrap gap-4 items-end mb-4">
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Дата от</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-40 px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Дата до</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-40 px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Тип стекла</label>
              <select
                value={glassType}
                onChange={(e) => setGlassType(e.target.value)}
                className="w-40 px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="">Все типы</option>
                <option value="прозрачный">Прозрачный</option>
                <option value="бронза">Бронза</option>
                <option value="графит">Графит</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Толщина (мм)</label>
              <select
                value={glassThickness}
                onChange={(e) => setGlassThickness(e.target.value)}
                className="w-28 px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="">Любая</option>
                <option value="4">4 мм</option>
                <option value="6">6 мм</option>
                <option value="8">8 мм</option>
                <option value="10">10 мм</option>
              </select>
            </div>
          </div>

          {/* Кнопка поиска */}
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={handleDetailSearch}
              disabled={loading || !detailWidth || !detailHeight}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                loading || !detailWidth || !detailHeight
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
            >
              {loading ? 'Поиск...' : 'Найти похожие'}
            </button>
            {(dateFrom || dateTo || glassType || glassThickness) && (
              <button
                type="button"
                onClick={() => {
                  setDateFrom('');
                  setDateTo('');
                  setGlassType('');
                  setGlassThickness('');
                }}
                className="px-4 py-2 text-purple-600 hover:text-purple-800 text-sm font-medium"
              >
                Сбросить фильтры
              </button>
            )}
          </div>
        </div>
      )}

      {/* Стандартная форма поиска по номеру заказа */}
      {searchMode === 'order' && (
      <form 
        className="search-form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSearch();
        }}
      >
          <div className="search-input-container">
            <input
              type="text"
              className="search-input"
              placeholder={hasFullAccess ? "Введите номер заказа (например: AKB1-24)" : (userBranch ? `Введите номер заказа (например: 24)` : "Определение филиала...")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={!hasFullAccess && !userBranch}
            />
            <button
              type="submit"
              className="search-button"
              disabled={loading || (!hasFullAccess && !userBranch)}
            >
              {loading ? "Загрузка..." : "Поиск"}
            </button>
          </div>
          
          
          
          
      </form>
      )}

      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Поиск заказов в цифровом рабочем месте 1036...</p>
        </div>
      )}
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      {searchResults.length > 0 && (
        <div className="search-results">
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-800">
              <strong>Найдено заказов:</strong> {searchResults.length}
            </p>
          </div>
          
          {searchResults.map((order) => (
            <div key={order.id} className="order-card">
              <div 
                className="order-header" 
                onClick={() => toggleOrderExpand(order.id, order.orderNumber)}
              >
                <div>
                  <h2 className="order-title">
                    Заказ #{order.orderNumber}
                  </h2>
                  <h3 className="order-subtitle">
                    {order.title}
                  </h3>
                  <p className="order-details">
                    ID: {order.id}
                  </p>
                  <div className="mt-2">
                    <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                      {order.stageTitle || order.stageName || order.stage || getStageName(order.stageId)}
                    </span>
                  </div>
                </div>
                <div className="expand-button">
                  {expandedOrder === order.id ? '▼' : '▶'}
                </div>
              </div>
              
              {expandedOrder === order.id && (
                <div className="order-content">
                  <hr className="order-divider" />
                  
                  {debugMode && (
                    <div className="bg-gray-100 p-2 mb-4 rounded text-xs overflow-auto">
                      <pre>
                        {JSON.stringify(order, null, 2)}
                      </pre>
                    </div>
                  )}

                  {/* Изменение стадии - только для admin */}
                  {userRole === 'admin' && (
                    <div className="mt-4 mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                      <h3 className="text-lg font-medium mb-3 text-orange-800">Изменить стадию заказа</h3>

                      {stageUpdateError && (
                        <div className="mb-3 p-2 bg-red-100 border border-red-300 text-red-700 rounded text-sm">
                          {stageUpdateError}
                        </div>
                      )}

                      <div className="flex flex-wrap gap-3 items-center">
                        <div className="flex-1 min-w-[200px]">
                          <label className="block text-sm font-medium text-orange-700 mb-1">
                            Текущая стадия: <span className="font-normal">{getStageName(order.stageId)}</span>
                          </label>
                          <select
                            className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            defaultValue={order.stageId}
                            onChange={(e) => {
                              if (e.target.value && e.target.value !== order.stageId) {
                                if (window.confirm(`Вы уверены, что хотите изменить стадию заказа на "${getStageName(e.target.value)}"?`)) {
                                  handleStageUpdate(order.id, e.target.value);
                                } else {
                                  e.target.value = order.stageId;
                                }
                              }
                            }}
                            disabled={updatingStage === order.id}
                          >
                            <optgroup label="Производство (Воронка 6)">
                              {adminStageOptions.filter(s => s.category === 'Производство').map(stage => (
                                <option key={stage.id} value={stage.id}>
                                  {stage.name} {stage.id === order.stageId ? '(текущая)' : ''}
                                </option>
                              ))}
                            </optgroup>
                            <optgroup label="Доставка (Воронка 7)">
                              {adminStageOptions.filter(s => s.category === 'Доставка').map(stage => (
                                <option key={stage.id} value={stage.id}>
                                  {stage.name} {stage.id === order.stageId ? '(текущая)' : ''}
                                </option>
                              ))}
                            </optgroup>
                          </select>
                        </div>

                        {updatingStage === order.id && (
                          <div className="flex items-center gap-2 text-orange-600">
                            <div className="animate-spin h-5 w-5 border-2 border-orange-500 border-t-transparent rounded-full"></div>
                            <span className="text-sm">Обновление...</span>
                          </div>
                        )}
                      </div>

                      <p className="mt-2 text-xs text-orange-600">
                        Выберите новую стадию из списка. Изменение вступит в силу немедленно.
                      </p>
                    </div>
                  )}

                  {/* Деталировка чертежа */}
                  {order.productionFiles && order.productionFiles.length > 0 && (
                    <div className="mt-4">
                      <h3 className="text-lg font-medium mb-2">Деталировка чертежа:</h3>
                      {order.productionFiles.map((file, index) => (
                        <div key={`prod-${file.id}-${index}`} className="mb-5 border rounded-lg p-3">
                          <div className="flex justify-between items-center mb-2">
                            <div>
                              <p className="font-medium">{file.name}</p>
                              <p className="text-xs text-gray-500">ID: {file.id}</p>
                            </div>
                            <div className="flex space-x-2">
                              <PrintButton
                                fileId={file.id}
                                fileUrl={file.url}
                                label="Печать"
                                color="blue"
                                overlayText={{
                                  orderNumber: order.orderNumber,
                                  topText: `Заказ №${order.orderNumber}`,
                                  bottomLines: [
                                    "Собрал заказ: ______________________________________________",
                                    "Выдал заказ:  ______________________________________________",
                                    "Принял заказ: ______________________________________________"
                                  ]
                                }}
                              />
                            </div>
                          </div>
                          
                          {fileErrors[file.id] ? (
                            <div className="bg-red-50 border border-red-300 text-red-700 p-3 rounded">
                              <p className="font-medium">Ошибка загрузки файла:</p>
                              <p className="text-sm">{fileErrors[file.id]}</p>
                              <p className="text-xs mt-2">
                                Попробуйте обновить файл или обратитесь к администратору.
                              </p>
                            </div>
                          ) : (
                            <FilePreview 
                              key={file.id} 
                              file={file} 
                              dealId={order.id}
                              index={index} 
                              totalFiles={order.productionFiles.length}
                              onError={(error) => handleFileError(file.id, error)}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Дизайн чертежа */}
                  {order.files && order.files.length > 0 && (
                    <div className="mt-6">
                      <h3 className="text-lg font-medium mb-2">Дизайн чертежа:</h3>
                      {order.files.map((file, index) => (
                        <div key={`main-${file.id}-${index}`} className="mb-5 border rounded-lg p-3">
                          <div className="flex justify-between items-center mb-2">
                            <div>
                              <p className="font-medium">{file.name}</p>
                              <p className="text-xs text-gray-500">ID: {file.id}</p>
                            </div>
                            <div className="flex space-x-2">
                              <PrintButton fileId={file.id} fileUrl={file.url} label="Печать" color="blue" />
                              {fileErrors[file.id] && (
                                <button 
                                  onClick={() => handleRefreshFile(file.id)}
                                  disabled={refreshingFile === file.id}
                                  className="text-xs bg-yellow-500 hover:bg-yellow-600 text-white py-1 px-2 rounded"
                                >
                                  {refreshingFile === file.id ? "Обновление..." : "Обновить файл"}
                                </button>
                              )}
                            </div>
                          </div>
                          
                          {fileErrors[file.id] ? (
                            <div className="bg-red-50 border border-red-300 text-red-700 p-3 rounded">
                              <p className="font-medium">Ошибка загрузки файла:</p>
                              <p className="text-sm">{fileErrors[file.id]}</p>
                              <p className="text-xs mt-2">
                                Попробуйте обновить файл или обратитесь к администратору.
                              </p>
                            </div>
                          ) : (
                            <FilePreview 
                              key={file.id} 
                              file={file} 
                              dealId={order.id}
                              index={index} 
                              totalFiles={order.files.length}
                              onError={(error) => handleFileError(file.id, error)}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Рейсы закалки */}
                  {(() => {
                    const trips = orderTrips[order.id];
                    if (!trips) return (
                      <div className="mt-4 bg-blue-50 p-3 rounded-lg">
                        <p className="text-blue-600 text-sm">⏳ Загружаем информацию о рейсах...</p>
                      </div>
                    );
                    if (trips.length === 0) return null;

                    const TRIP_STATUS_LABEL = {
                      saved: { label: 'Создан', color: 'bg-gray-100 text-gray-700' },
                      shipped: { label: 'Отправлен к партнёру', color: 'bg-blue-100 text-blue-700' },
                      delivered_to_partner: { label: 'Сдан партнёру закалки', color: 'bg-orange-100 text-orange-700' },
                      received_from_partner: { label: 'Получен от партнёра', color: 'bg-purple-100 text-purple-700' },
                      received: { label: 'Принят на склад', color: 'bg-green-100 text-green-700' },
                    };

                    return (
                      <div className="mt-4">
                        <h3 className="text-lg font-medium mb-2">🚛 Рейсы закалки:</h3>
                        <div className="space-y-2">
                          {trips.map(trip => {
                            const st = TRIP_STATUS_LABEL[trip.status] || { label: trip.status, color: 'bg-gray-100 text-gray-700' };
                            const zakalkaDate = trip.shippedDate || trip.deliveredToPartnerDate;
                            return (
                              <div key={trip.id} className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-indigo-800">Рейс {trip.tripNumber}</span>
                                    {trip.partner && (
                                      <span className="text-sm text-gray-500">• {trip.partner}</span>
                                    )}
                                  </div>
                                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${st.color}`}>
                                    {st.label}
                                  </span>
                                </div>
                                {zakalkaDate && (
                                  <p className="text-sm text-gray-600 mt-1">
                                    Передан на закалку:{' '}
                                    <span className="font-medium">
                                      {new Date(zakalkaDate).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}

                  {/* История изменений стадий (Timeline) */}
                  <div className="mt-6">
                    <h3 className="text-lg font-medium mb-2">📈 История изменений стадий:</h3>
                    {orderTimeline[order.id] ? (
                      orderTimeline[order.id].length > 0 ? (
                        <div className="space-y-2">
                          {orderTimeline[order.id].map((event, index) => (
                            <div key={index} className="bg-gray-50 border rounded-lg p-3">
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <p className="font-medium text-blue-800">
                                    {event.stageName || getStageName(event.stageId) || 'Изменение стадии'}
                                  </p>
                                  {event.description && (
                                    <p className="text-sm text-gray-600 mt-1">{event.description}</p>
                                  )}
                                </div>
                                <div className="text-right">
                                  <p className="text-xs text-gray-500">
                                    {new Date(event.created).toLocaleString('ru-RU')}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-600 text-sm bg-gray-50 p-3 rounded-lg">
                          История изменений стадий для этого заказа не найдена
                        </p>
                      )
                    ) : (
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <p className="text-blue-600 text-sm">⏳ Загружаем историю изменений стадий...</p>
                      </div>
                    )}
                  </div>

                  {/* Сообщение, если файлов нет */}
                  {(!order.productionFiles || order.productionFiles.length === 0) && 
                   (!order.files || order.files.length === 0) && (
                    <p className="no-files-message">
                      Файлы для этого заказа не найдены
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Результаты поиска по деталям */}
      {detailSearchResults.length > 0 && (
        <div className="search-results">
          <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
            <p className="text-sm text-purple-800">
              <strong>Найдено заказов с похожими деталями:</strong> {detailSearchResults.length}
            </p>
            <p className="text-xs text-purple-600 mt-1">
              Поиск: {detailWidth}×{detailHeight}мм (±{tolerance}мм)
              {glassType && <span className="ml-2">• Тип: {glassType}</span>}
              {glassThickness && <span className="ml-2">• Толщина: {glassThickness}мм</span>}
              {dateFrom && <span className="ml-2">• От: {dateFrom}</span>}
              {dateTo && <span className="ml-2">• До: {dateTo}</span>}
            </p>
          </div>

          {detailSearchResults.map((order) => (
            <div key={order.id} className="order-card border-l-4 border-purple-500">
              <div
                className="order-header"
                onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h2 className="order-title">
                      Заказ #{order.orderNumber}
                    </h2>
                    <span className={`px-3 py-1 rounded-full text-sm font-bold ${getSimilarityColor(order.bestMatch.similarity)}`}>
                      {order.bestMatch.similarity}% совпадение
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Лучшее совпадение: <span className="font-medium">{order.bestMatch.text}</span>
                    <span className="text-purple-600 ml-2">({order.bestMatch.glassType})</span>
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Найдено {order.matchCount} похожих деталей • ID: {order.id}
                  </p>
                </div>
                <div className="expand-button">
                  {expandedOrder === order.id ? '▼' : '▶'}
                </div>
              </div>

              {expandedOrder === order.id && (
                <div className="order-content p-4 bg-gray-50">
                  <h4 className="font-medium text-gray-800 mb-3">🎯 Найденные совпадения:</h4>
                  <div className="space-y-2 mb-4">
                    {order.allMatches.map((match, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-white rounded-lg border-2 border-purple-300"
                      >
                        <div className="flex-1">
                          <p className="font-medium text-gray-800">{match.text}</p>
                          <div className="flex gap-2 mt-1 flex-wrap">
                            <span className="text-xs px-2 py-1 bg-gray-100 rounded">
                              {match.glassType}
                            </span>
                            <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                              {match.dimensions.width}×{match.dimensions.height}мм
                            </span>
                            {match.dimensions.quantity > 1 && (
                              <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded">
                                {match.dimensions.quantity} шт
                              </span>
                            )}
                          </div>
                        </div>
                        <div className={`ml-3 px-3 py-1 rounded-full text-sm font-bold ${getSimilarityColor(match.similarity)}`}>
                          {match.similarity}%
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Все детали заказа */}
                  {order.allDetails && order.allDetails.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-medium text-gray-800 mb-3">📋 Все детали в заказе:</h4>
                      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-3 py-2 text-left font-medium text-gray-700">Тип стекла</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-700">Размер</th>
                              <th className="px-3 py-2 text-center font-medium text-gray-700">Кол-во</th>
                            </tr>
                          </thead>
                          <tbody>
                            {order.allDetails.map((detail, index) => {
                              const isMatch = order.allMatches.some(m =>
                                m.dimensions.width === detail.width &&
                                m.dimensions.height === detail.height &&
                                m.glassType === detail.glassType
                              );
                              return (
                                <tr
                                  key={index}
                                  className={`border-t ${isMatch ? 'bg-purple-50' : ''}`}
                                >
                                  <td className="px-3 py-2 text-gray-800">{detail.glassType}</td>
                                  <td className="px-3 py-2">
                                    <span className={`${isMatch ? 'font-medium text-purple-700' : 'text-gray-700'}`}>
                                      {detail.width}×{detail.height}мм
                                    </span>
                                    {isMatch && <span className="ml-2 text-purple-500">✓</span>}
                                  </td>
                                  <td className="px-3 py-2 text-center text-gray-700">{detail.quantity || 1}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Файлы чертежей */}
                  {order.productionFiles && order.productionFiles.length > 0 && (
                    <div className="mt-4">
                      <h4 className="font-medium text-gray-800 mb-2">📐 Чертежи для производства:</h4>
                      <div className="files-grid">
                        {order.productionFiles.map((file, index) => (
                          <div key={`prod-${file.id}-${index}`} className="file-item">
                            <FilePreview
                              file={file}
                              dealId={order.id}
                              index={index}
                              totalFiles={order.productionFiles.length}
                              onError={(error) => handleFileError(file.id, error)}
                            />
                            <PrintButton
                              fileId={file.id}
                              fileUrl={file.url}
                              fileName={file.name}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {order.files && order.files.length > 0 && (
                    <div className="mt-4">
                      <h4 className="font-medium text-gray-800 mb-2">📄 Основные чертежи:</h4>
                      <div className="files-grid">
                        {order.files.map((file, index) => (
                          <div key={`main-${file.id}-${index}`} className="file-item">
                            <FilePreview
                              file={file}
                              dealId={order.id}
                              index={index}
                              totalFiles={order.files.length}
                              onError={(error) => handleFileError(file.id, error)}
                            />
                            <PrintButton
                              fileId={file.id}
                              fileUrl={file.url}
                              fileName={file.name}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {(!order.files || order.files.length === 0) &&
                   (!order.productionFiles || order.productionFiles.length === 0) && (
                    <p className="text-gray-500 text-sm mt-4">Файлы чертежей не найдены</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchOrdersPageB24;
